import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import { getNicknameFormState } from '@lib/utils/nickname';
import { showToast } from '@lib/utils/toastmaster';
import { ActionButton } from '@pages/ActionButton';
import { AuthField } from '@pages/Auth/AuthField';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import css from './ChangeNickname.module.scss';

const NICKNAME_MAX_LENGTH = 64;

export const ChangeNicknameForm = observer(
  ({
    mobile,
    onSaved,
    autoFocus,
  }: {
    mobile?: boolean;
    /** Called after a successful save (e.g. to close a host modal). */
    onSaved?: () => void;
    autoFocus?: boolean;
  }) => {
    const storedNickname = userStore.data?.name?.nickname ?? '';
    const [nickname, setNickname] = useState(() => storedNickname);
    const [loading, setLoading] = useState(false);
    const seededRef = useRef(userStore.data != null);
    const autoFocusOnDesktop = useDesktopAutoFocus();

    // One-shot seed when userStore.data lands after mount (fresh page loads).
    useEffect(() => {
      if (seededRef.current) return;
      if (!userStore.data) return;
      seededRef.current = true;
      const current = userStore.data.name?.nickname;
      if (current) setNickname((existing) => (existing ? existing : current));
    }, [userStore.data]);

    const { trimmed, canSubmit } = getNicknameFormState(nickname, storedNickname, loading);

    const submit = async () => {
      if (!canSubmit) return;
      setLoading(true);
      try {
        await userStore.updateNickname(trimmed);
        showToast('nickname-changed', { nickname: trimmed });
        onSaved?.();
      } catch (error) {
        showToast('nickname-change-failed', { error });
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className={classNames(css.changeNicknameContainer, { [css.mobile]: mobile })}>
        <div className={css.changeNicknameInnerContainer}>
          <form
            className={css.changeNicknameForm}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <AuthField
              label="Nickname"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              slotProps={{
                htmlInput: {
                  maxLength: NICKNAME_MAX_LENGTH,
                  autoComplete: 'nickname',
                  // Compose caller intent externally: an absent `autoFocus` prop
                  // (e.g. the Settings page) stays falsy, so it doesn't focus.
                  autoFocus: autoFocus && autoFocusOnDesktop,
                },
              }}
            />
            <div className={css.submitContainer}>
              <div className={css.innerSubmitContainer}>
                <ActionButton type="submit" disabled={!canSubmit} fullWidth={mobile}>
                  {loading ? 'Saving...' : 'Change nickname'}
                </ActionButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  },
);
